var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { dia, layout, ui } from '@joint/plus';
import { ShelfElement, ProductElement, getAllProducts, getAllShelves, ProductCategories, storeItemsConfig, cellNamespace } from './shapes';
import { addElementTools, removeElementTools } from './tools';
import { validateChangePosition, validateChangeSize, isSizeValid, isPositionValid } from './validators';
import exampleGraph from '../assets/example.json';
var grid = storeItemsConfig.grid;
var selectedCellView = null;
export var init = function () {
    var canvasElement = document.getElementById('canvas');
    var stencilElement = document.getElementById('products-stencil');
    var shelvesStencilElement = document.getElementById('shelves-stencil');
    var graph = new dia.Graph({}, { cellNamespace: cellNamespace });
    var paper = new dia.Paper({
        width: 25 * grid,
        height: 20 * grid,
        model: graph,
        gridSize: grid,
        drawGrid: { name: 'mesh', color: '#d4d4d4' },
        background: { color: '#FBFBFB' },
        embeddingMode: true,
        moveThreshold: 10,
        clickThreshold: 10,
        validateEmbedding: function (childView, parentView) {
            if (childView.model instanceof ShelfElement) {
                return false;
            }
            if (parentView.model instanceof ProductElement) {
                return false;
            }
            return true;
        },
        frontParentOnly: true,
        findParentBy: function (elementView) {
            var area = elementView.model.getBBox();
            return graph.findModelsInArea(area).filter(function (model) {
                if (model instanceof ProductElement) {
                    return true;
                }
                return model.getBBox().containsRect(area);
            });
        },
        async: true,
        cellViewNamespace: cellNamespace,
        highlighting: {
            embedding: {
                name: 'stroke',
                options: {
                    layer: 'front',
                    padding: 0,
                    attrs: {
                        'stroke': '#0058FF',
                        'stroke-width': 3
                    }
                }
            }
        },
        interactive: function (_a) {
            var model = _a.model;
            var isChildOfSelectedElement = selectedCellView
                ? model.isEmbeddedIn(selectedCellView.model)
                : false;
            return {
                stopDelegation: !isChildOfSelectedElement
            };
        }
    });
    var scroller = new ui.PaperScroller({
        paper: paper,
        autoResizePaper: true,
        cursor: 'grab',
        baseWidth: 25 * grid,
        baseHeight: 20 * grid,
        scrollWhileDragging: true,
        borderless: true,
        padding: 300
    });
    canvasElement.appendChild(scroller.render().el);
    var commandManager = new dia.CommandManager({
        graph: graph
    });
    var validator = new dia.Validator({
        commandManager: commandManager,
        cancelInvalid: true
    });
    var createStencilGroups = function () {
        var products = storeItemsConfig.products;
        var groups = {};
        var getLayoutOptions = function (columnsCount) {
            return {
                columns: columnsCount,
                columnWidth: 200 / columnsCount,
                rowHeight: 'compact'
            };
        };
        Object.keys(products).forEach(function (categoryName, idx) {
            var maxWidth = products[categoryName].reduce(function (acc, product) { return Math.max(acc, product.width); }, 0);
            groups[categoryName] = {
                layout: getLayoutOptions(5 - maxWidth),
                closed: idx > 3,
                index: idx + 1,
                label: ProductCategories[categoryName].toLowerCase()
            };
        });
        return groups;
    };
    var getStencilPaperOptions = function () {
        return {
            model: new dia.Graph({}, { cellNamespace: cellNamespace }),
            sorting: dia.Paper.sorting.NONE,
            cellViewNamespace: cellNamespace
        };
    };
    var productsStencil = new ui.Stencil({
        paper: scroller,
        width: 240,
        scaleClones: true,
        dropAnimation: true,
        usePaperGrid: true,
        theme: 'modern',
        groups: createStencilGroups(),
        paperOptions: function () { return getStencilPaperOptions(); },
        layout: function (groupGraph, group) {
            return layout.GridLayout.layout(groupGraph, __assign({ marginY: 20, marginX: 20, rowGap: 20 }, group.layout));
        },
        search: function (product, keyword, group) {
            return product.match(group, keyword);
        }
    });
    stencilElement.appendChild(productsStencil.el);
    productsStencil.render();
    productsStencil.load(getAllProducts());
    var shelvesStencil = new ui.Stencil({
        paper: scroller,
        width: 340,
        height: 850,
        scaleClones: true,
        dropAnimation: true,
        usePaperGrid: true,
        theme: 'modern',
        layout: false,
        paperOptions: function () { return getStencilPaperOptions(); }
    });
    shelvesStencilElement.appendChild(shelvesStencil.el);
    shelvesStencil.render();
    shelvesStencil.load(getAllShelves());
    graph.fromJSON(exampleGraph);
    scroller.centerContent({ useModelGeometry: true });
    // Register Events
    validator.validate('change:position', validateChangePosition(graph));
    validator.validate('change:size', validateChangeSize(graph));
    validator.on('invalid', function (err) {
        var cellView = err.cell.findView(paper);
        if (cellView) {
            cellView.vel.removeClass('invalid');
        }
    });
    graph.on({
        'batch:stop': function (batch) {
            var _a = batch, cell = _a.cell, batchName = _a.batchName;
            if (batchName !== 'resize')
                return;
            var cellView = cell.findView(paper);
            cellView.vel.toggleClass('invalid', !isSizeValid(graph, cell));
        }
    });
    paper.on({
        'blank:pointerdown': function (evt) {
            unsetElement(paper);
            scroller.startPanning(evt);
        },
        'element:pointermove': function (elementView, evt) {
            var data = evt.data;
            if (!data.moved) {
                data.currentCellView = elementView.getDelegatedView();
                data.moved = true;
                unsetElement(paper);
            }
            var delegatedView = data.currentCellView;
            delegatedView.vel.toggleClass('invalid', !isPositionValid(graph, delegatedView.model));
        },
        'element:pointerup': function (elementView, evt) {
            if (evt.data.moved) {
                setElement(evt.data.currentCellView);
            }
            else {
                setElement(elementView);
            }
        },
        'element:pointerdblclick': function (elementView) {
            var element = elementView.model;
            var parent = element.getParentCell();
            if (parent) {
                setElement(parent.findView(paper));
            }
        }
    });
    var stencilEventMap = function (stencil) {
        return {
            'element:dragstart': function () {
                unsetElement(paper);
            },
            'element:drag': function (cloneView, evt, _dropArea, validDropTarget) {
                if (validDropTarget) {
                    cloneView.vel.toggleClass('invalid', !isPositionValid(graph, cloneView.model));
                    cloneView.vel.removeAttr('opacity');
                }
                else {
                    cloneView.vel.removeClass('invalid');
                    cloneView.vel.attr('opacity', 0.5);
                }
            },
            'element:dragend': function (cloneView, _evt) {
                if (cloneView.vel.hasClass('invalid')) {
                    stencil.cancelDrag({ dropAnimation: true });
                }
            },
            'element:drop': function (elementView) {
                setElement(elementView);
            }
        };
    };
    productsStencil.on(stencilEventMap(productsStencil));
    shelvesStencil.on(stencilEventMap(shelvesStencil));
};
var setElement = function (cellView) {
    selectedCellView = cellView;
    addElementTools(cellView);
};
var unsetElement = function (paper) {
    selectedCellView = null;
    removeElementTools(paper);
};
